import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePWAInstall } from '../hooks/usePWAInstall';
import Button from '../components/Button';
import { Download as DownloadIcon, ArrowLeft, CheckCircle, X, Smartphone } from 'lucide-react';
import styled from 'styled-components';

const InstallContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem;
  text-align: center;
`;

const InstallButton = styled(Button)`
  margin: 1.5rem 0;
  padding: 0.75rem 2rem;
  font-size: 1.1rem;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${({ theme }) => theme.colors.primaryDark};
    transform: translateY(-2px);
  }

  &:disabled {
    background: ${({ theme }) => theme.colors.gray300};
    cursor: not-allowed;
    transform: none;
  }
`;

const BackButton = styled(Button)`
  position: absolute;
  top: 1rem;
  left: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: transparent;
  color: ${({ theme }) => theme.colors.primary};
  padding: 0.5rem 1rem;
  
  &:hover {
    background: ${({ theme }) => theme.colors.gray100};
  }
`;

const Message = styled.div<{ type: 'success' | 'error' }>`
  margin: 1rem 0;
  padding: 1rem;
  border-radius: 6px;
  background: ${({ theme, type }) => 
    type === 'success' ? theme.colors.successLight : theme.colors.errorLight};
  color: ${({ theme, type }) => 
    type === 'success' ? theme.colors.success : theme.colors.error};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
`;

const InstallPage: React.FC = () => {
  const navigate = useNavigate();
  const { 
    canInstall, 
    isInstalled, 
    isIOSDevice, 
    isAndroidDevice, 
    isMobile,
    handleInstallClick 
  } = usePWAInstall();
  const [status, setStatus] = useState<'idle' | 'installing' | 'success' | 'error'>('idle');
  const [error, setError] = useState('');

  const handleInstall = async () => {
    if (!canInstall) {
      setError('Your browser does not support installing this app.');
      return;
    }

    try {
      setStatus('installing');
      setError('');
      
      const installed = await handleInstallClick();
      
      if (installed) {
        setStatus('success');
      } else {
        setStatus('error');
        setError('Installation was cancelled or failed. Please try again.');
      }
    } catch (err) {
      console.error('Installation error:', err);
      setStatus('error');
      setError('An error occurred during installation. Please try again.');
    }
  };

  if (isInstalled) {
    return (
      <InstallContainer>
        <Message type="success">
          <CheckCircle size={24} />
          <span>App installed successfully! You can now access it from your home screen.</span>
        </Message>
        <Button onClick={() => navigate('/')}>
          Go to Dashboard
        </Button>
      </InstallContainer>
    );
  }

  return (
    <InstallContainer>
      <BackButton onClick={() => navigate(-1)}>
        <ArrowLeft size={20} /> Back
      </BackButton>
      
      <h1>Install FMS App</h1>
      <p>Get the best experience by installing our app on your device</p>

      <div style={{ margin: '2rem 0' }}>
        <InstallButton 
          onClick={handleInstall}
          disabled={!canInstall || status === 'installing'}
          title={canInstall ? 'Install FMS App' : 'Installation not supported in this browser'}
        >
          {status === 'installing' ? (
            'Installing...'
          ) : (
            <>
              <DownloadIcon size={20} /> Install FMS App
            </>
          )}
        </InstallButton>
        
        {status === 'success' && (
          <Message type="success">
            <CheckCircle size={20} /> 
            Installation started! Please follow the prompts to complete the installation.
          </Message>
        )}
        
        {error && (
          <Message type="error">
            <X size={20} /> {error}
          </Message>
        )}
      </div>

      {!canInstall && (
        <div style={{ marginTop: '2rem', textAlign: 'left' }}>
          <h3>How to install:</h3>
          <p>Your browser may support installing this app through its menu:</p>
          <ul style={{ textAlign: 'left', marginTop: '0.5rem' }}>
            {isIOSDevice && (
              <li><strong>iOS Safari:</strong> Tap the share icon <Smartphone size={16} /> → "Add to Home Screen"</li>
            )}
            {isAndroidDevice && (
              <li><strong>Android Chrome:</strong> Tap the three-dot menu → "Install app" or "Add to Home Screen"</li>
            )}
            {!isMobile && (
              <>
                <li><strong>Chrome/Edge:</strong> Click the three-dot menu → "Install" or look for an install icon in the address bar</li>
                <li><strong>Firefox:</strong> Click the three-line menu → "Install"</li>
              </>
            )}
          </ul>
        </div>
      )}
    </InstallContainer>
  );
};

export default InstallPage;